import {
  Select as MuiSelect,
  MenuItem,
  Theme,
  ListSubheader,
  Box,
  Stack,
  Skeleton,
} from '@mui/material';
import { ChangeEvent, FC, useMemo } from 'react';
import FormControl from '../form-control';
import { FormProps, getMuiSize } from '../helpers';
import { UiOption } from '@/lib/helpers';
import { createUseStyles } from 'react-jss';
import Text from '@/components/text';
import classNames from 'classnames';
import Chip from '@/components/chip';

interface Props extends FormProps {
  value: string | undefined;
  options: UiOption[];
  onChange: (value: string | undefined, event: ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  loading?: boolean;
}

const useStyles = createUseStyles((theme: Theme) => ({
  select: {
    backgroundColor: theme.palette.common.white,
    '&.disabled': {
      backgroundColor: theme.palette.subtleGray,
      '& fieldset': {
        borderColor: `${theme.palette.subtleGray} !important`,
      },
      '& .MuiSelect-select': {
        color: theme.palette.lightGray,
      },
    },
    '& fieldset': {
      borderWidth: '1px !important',
    },
  },
  subheader: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    lineHeight: '30px',
    marginTop: 10,
    paddingLeft: 8,
  },
  menuItem: {
    whiteSpace: 'wrap',
  },
  emptyOption: {
    width: '100%',
    borderRadius: 4,
    color: theme.palette.darkGray,
    height: `21px !important`,
  },
  emptySelected: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    borderRadius: 4,
    color: theme.palette.darkGray,
    height: `100% !important`,
  },
}));

const Select: FC<Props> = ({
  name,
  value = '',
  options,
  label = '',
  disabled = false,
  autoFocus = false,
  onChange,
  error = '',
  size = 'medium',
  fullWidth = false,
  readonly = false,
  required = false,
  withNotSet = false,
  className = '',
  loading = false,
  emptyMessage = '',
}) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    const value = withNotSet && rawValue === '' ? undefined : String(rawValue);
    onChange(value, event);
  };

  const labelId = `${name}-label`;
  const styles = useStyles();
  const labelFormatted = required && typeof label === 'string' ? `${label} *` : label;
  const isDisabled = disabled || readonly;
  const hasEmptyMessge = !!emptyMessage;
  const displayEmpty = !isDisabled && (withNotSet || hasEmptyMessge);
  const displayEmptyLabel = emptyMessage || 'Not Set';

  const compOptions = useMemo(() => {
    if (!displayEmpty) {
      return options;
    }

    const extraOption: UiOption | null = {
      label: <Chip label={displayEmptyLabel} size="small" className={styles.emptyOption} />,
      value: '',
    };

    return [extraOption, ...options];
  }, [displayEmpty, displayEmptyLabel, options, styles.emptyOption]);

  const compClass = classNames(className, styles.select, { disabled: isDisabled });

  const SelectEl = (
    <MuiSelect
      labelId={labelId}
      id={name}
      name={name}
      value={loading ? '' : value || ''}
      displayEmpty={displayEmpty}
      label={labelFormatted}
      onChange={handleChange}
      disabled={isDisabled}
      autoFocus={autoFocus}
      error={!!error}
      size={getMuiSize(size)}
      fullWidth={fullWidth}
      className={compClass}
      required={required}
      renderValue={(selected) => {
        const option = compOptions.find(({ value }) => value === selected);
        if (!selected && displayEmpty) {
          return <Chip label={displayEmptyLabel} size="small" className={styles.emptySelected} />;
        }
        return option?.label || '';
      }}
      MenuProps={{
        PaperProps: {
          style: {
            maxWidth: 200,
            width: '100%',
          },
        },
      }}
    >
      {compOptions.map(({ value, label, description, disabled = false, meta = {}, key }) => {
        if (meta.header) {
          return (
            <ListSubheader className={styles.subheader} key={key || value}>
              {label}
            </ListSubheader>
          );
        }

        const hasDescription = !!description;

        return (
          <MenuItem
            key={key || value}
            value={value}
            disabled={disabled}
            className={styles.menuItem}
          >
            <Stack width="100%">
              <Box>
                <Text color="black" dotdot>
                  {label}
                </Text>
              </Box>
              {hasDescription && (
                <Box mt={0.5}>
                  <Text size="small" color="grey">
                    {description}
                  </Text>
                </Box>
              )}
            </Stack>
          </MenuItem>
        );
      })}
    </MuiSelect>
  );

  return (
    <FormControl label={labelFormatted} error={error} name={name} size={size}>
      {loading ? (
        <Skeleton variant="rounded" width="100%">
          {SelectEl}
        </Skeleton>
      ) : (
        SelectEl
      )}
    </FormControl>
  );
};

export default Select;
